<template>
  <section class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
    <div>
      <h2 class="text-lg font-semibold text-gray-900">
        Paramètres messagerie
      </h2>
    </div>

    <div v-if="showDomainSettings && (!domain || !domain.chat)" class="mt-6">
      <b-message type="is-warning" has-icon>
        Impossible de charger les paramètres de messagerie pour ce domaine.
      </b-message>
    </div>

    <template v-else-if="showDomainSettings">
      <div class="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div class="rounded-lg border border-gray-200 p-4">
          <h3 class="text-base font-medium text-gray-900">
            Module contact
          </h3>

          <div v-if="domain.contact" class="mt-4 space-y-4">
            <label class="flex items-center">
              <t-toggle
                v-model="mavieproContactActivated"
                :value="true"
                :uncheckedValue="false"
              />
              <span class="ml-3 text-sm">
                Messagerie {{ applicationDisplayLabel }}
              </span>
            </label>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label for="chatDomainContactEmail" class="block text-sm font-medium leading-6 text-gray-900">
                  Adresse e-mail {{ cseLabel }}
                </label>
                <input
                  id="chatDomainContactEmail"
                  v-model="domain.contact.email"
                  type="email"
                  class="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-900 sm:text-sm sm:leading-6"
                />
              </div>
              <div>
                <label for="chatDomainContactPhone" class="block text-sm font-medium leading-6 text-gray-900">
                  Téléphone
                </label>
                <input
                  id="chatDomainContactPhone"
                  v-model="domain.contact.phone"
                  type="text"
                  class="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-900 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div class="max-w-sm">
              <label for="chatDomainContactDenomination" class="block text-sm font-medium leading-6 text-gray-900">
                Dénomination
              </label>
              <input
                id="chatDomainContactDenomination"
                v-model.trim="contactDenomination"
                type="text"
                class="mt-2 block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-900 sm:text-sm sm:leading-6"
                :placeholder="defaultCseLabel"
              />
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-200 p-4">
          <h3 class="text-base font-medium text-gray-900">
            Délais de réponse
          </h3>

          <div class="mt-4 grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium leading-6 text-gray-900">
                Délai réponse interlocuteurs
              </label>
              <select
                v-model.number="domain.chat.interlocutor_response_delay_hours"
                class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              >
                <option v-for="delay in responseDelayOptions" :key="'interlocutor-delay-' + delay" :value="delay">
                  {{ delay }}h
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium leading-6 text-gray-900">
                Délai réponse {{ cseLabel }}
              </label>
              <select
                v-model.number="domain.chat.cse_response_delay_hours"
                class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              >
                <option v-for="delay in responseDelayOptions" :key="'cse-delay-' + delay" :value="delay">
                  {{ delay }}h
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          class="inline-flex justify-center rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="savingDomain"
          @click="saveMessagingDomainSettings"
        >
          {{ savingDomain ? 'Enregistrement...' : 'Sauvegarder les paramètres du domaine' }}
        </button>
      </div>
    </template>

    <div v-if="showGlobalTemplates" class="mt-6 rounded-lg border border-blue-200 bg-blue-50/60 p-4">
        <h3 class="text-base font-medium text-gray-900">
          Templates globaux
        </h3>
        <p class="mt-1 text-sm text-gray-600">
          Ces textes sont communs à tous les domaines de l'application.
        </p>

        <div class="mt-4 max-w-sm">
          <label class="block text-sm font-medium leading-6 text-gray-900">
            Délai réponse {{ applicationDisplayLabel }}
          </label>
          <select
            v-model.number="messagingSettings.maviepro_response_delay_hours"
            class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
          >
            <option v-for="delay in responseDelayOptions" :key="'maviepro-delay-' + delay" :value="delay">
              {{ delay }}h
            </option>
          </select>
        </div>

        <div class="mt-1 text-sm text-gray-600">
          <span>Variables disponibles :</span>
          <div class="mt-2 flex flex-wrap gap-2">
            <button
              v-for="token in availableVariableTokens"
              :key="token"
              type="button"
              class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition"
              :class="isTokenDisabled(token)
                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                : 'border-blue-200 bg-white text-blue-700 hover:border-blue-300 hover:bg-blue-50'"
              :disabled="isTokenDisabled(token)"
              :title="tokenTitle(token)"
              @mousedown.prevent="insertVariable(token)"
              @keydown.enter.prevent="insertVariable(token)"
              @keydown.space.prevent="insertVariable(token)"
            >
              {{ token }}
            </button>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900">
              Note interlocuteur
            </label>
            <textarea
              v-model="messagingSettings.interlocutor_note_text"
              rows="4"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              placeholder="Ce message sera envoyé uniquement à {nom de l'interlocuteur}..."
              @focus="rememberFieldState('interlocutor_note_text', $event)"
              @click="rememberFieldState('interlocutor_note_text', $event)"
              @keyup="rememberFieldState('interlocutor_note_text', $event)"
              @select="rememberFieldState('interlocutor_note_text', $event)"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900">
              Note {{ cseLabel }}
            </label>
            <textarea
              v-model="messagingSettings.cse_note_text"
              rows="4"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              :placeholder="`Ce message sera envoyé uniquement à ${cseVariableToken}...`"
              @focus="rememberFieldState('cse_note_text', $event)"
              @click="rememberFieldState('cse_note_text', $event)"
              @keyup="rememberFieldState('cse_note_text', $event)"
              @select="rememberFieldState('cse_note_text', $event)"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900">
              Note {{ applicationDisplayLabel }}
            </label>
            <textarea
              v-model="messagingSettings.maviepro_note_text"
              rows="4"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              :placeholder="`Ce message sera envoyé à l'équipe ${applicationDisplayLabel}...`"
              @focus="rememberFieldState('maviepro_note_text', $event)"
              @click="rememberFieldState('maviepro_note_text', $event)"
              @keyup="rememberFieldState('maviepro_note_text', $event)"
              @select="rememberFieldState('maviepro_note_text', $event)"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900">
              Accusé auto {{ cseLabel }}
            </label>
            <textarea
              v-model="messagingSettings.cse_auto_reply_text"
              rows="4"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              placeholder="Bonjour, nous avons bien reçu votre demande..."
              @focus="rememberFieldState('cse_auto_reply_text', $event)"
              @click="rememberFieldState('cse_auto_reply_text', $event)"
              @keyup="rememberFieldState('cse_auto_reply_text', $event)"
              @select="rememberFieldState('cse_auto_reply_text', $event)"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900">
              Accusé auto {{ applicationDisplayLabel }}
            </label>
            <textarea
              v-model="messagingSettings.maviepro_auto_reply_text"
              rows="4"
              class="mt-2 block w-full rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
              :placeholder="`Votre demande a bien été reçue par l'équipe ${applicationDisplayLabel}...`"
              @focus="rememberFieldState('maviepro_auto_reply_text', $event)"
              @click="rememberFieldState('maviepro_auto_reply_text', $event)"
              @keyup="rememberFieldState('maviepro_auto_reply_text', $event)"
              @select="rememberFieldState('maviepro_auto_reply_text', $event)"
            ></textarea>
          </div>
        </div>

        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="savingTemplates"
            @click="saveMessagingTemplates"
          >
            {{ savingTemplates ? 'Enregistrement...' : 'Sauvegarder les templates globaux' }}
          </button>
        </div>
      </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import { defaultContactDenomination, resolveContactDenomination } from '@/utils/contactDenomination'

export default {
  name: 'MessagingSettingsPanel',
  props: {
    showDomainSettings: {
      type: Boolean,
      default: true
    },
    showGlobalTemplates: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      responseDelayOptions: [24, 36, 48, 72],
      messagingSettings: {
        maviepro_response_delay_hours: 48,
        interlocutor_note_text: '',
        cse_note_text: '',
        maviepro_note_text: '',
        cse_auto_reply_text: '',
        maviepro_auto_reply_text: ''
      },
      activeTemplateField: null,
      savingDomain: false,
      savingTemplates: false
    }
  },
  computed: {
    ...mapGetters([
      'application',
      'domain'
    ]),
    isTractApplication () {
      return String(this.application?.app?.slug || this.application?.slug || '').toLowerCase().trim() === 'tract'
    },
    applicationDisplayLabel () {
      if (this.isTractApplication) {
        return 'Tract'
      }

      return 'Maviepro'
    },
    defaultCseLabel () {
      return defaultContactDenomination(this.application)
    },
    cseLabel () {
      if (this.showDomainSettings) {
        return resolveContactDenomination(this.domain, this.application)
      }

      return this.defaultCseLabel
    },
    cseVariableLabel () {
      return this.isTractApplication ? 'nom du Syndicat' : 'nom du CSE'
    },
    cseVariableToken () {
      return this.isTractApplication ? '{nom du Syndicat}' : '{nom du CSE}'
    },
    availableVariableTokens () {
      return [
        '{nom de l\'interlocuteur}',
        this.cseVariableToken,
        '{délai de réponse}',
        '{application}'
      ]
    },
    templateVariableMap () {
      return {
        interlocutor_note_text: [
          '{nom de l\'interlocuteur}',
          '{délai de réponse}',
          '{application}'
        ],
        cse_note_text: [
          this.cseVariableToken,
          '{délai de réponse}',
          '{application}'
        ],
        maviepro_note_text: [
          '{délai de réponse}',
          '{application}'
        ],
        cse_auto_reply_text: [
          this.cseVariableToken,
          '{délai de réponse}',
          '{application}'
        ],
        maviepro_auto_reply_text: [
          '{délai de réponse}',
          '{application}'
        ]
      }
    },
    activeTemplateFieldKey () {
      return this.activeTemplateField?.key || null
    },
    contactDenomination: {
      get () {
        return resolveContactDenomination(this.domain, this.application)
      },
      set (value) {
        if (!this.domain?.contact) {
          return
        }

        const normalizedValue = String(value || '').trim()
        this.$set(this.domain.contact, 'denomination', normalizedValue || this.defaultCseLabel)
      }
    },
    mavieproContactActivated: {
      get () {
        return this.domain?.chat?.is_maviepro_contact_activated !== false
      },
      set (value) {
        if (!this.domain?.chat) {
          return
        }

        this.$set(this.domain.chat, 'is_maviepro_contact_activated', Boolean(value))
      }
    }
  },
  watch: {
    application: {
      handler (application) {
        this.syncMessagingSettings(application)
      },
      immediate: true
    }
  },
  methods: {
    tokensForField (fieldKey) {
      return this.templateVariableMap[fieldKey] || []
    },
    fieldSupportsToken (fieldKey, token) {
      return this.tokensForField(fieldKey).includes(token)
    },
    isTokenDisabled (token) {
      if (!this.activeTemplateFieldKey) {
        return false
      }

      return !this.fieldSupportsToken(this.activeTemplateFieldKey, token)
    },
    tokenTitle (token) {
      if (!this.activeTemplateFieldKey) {
        return 'Sélectionnez un champ pour insérer cette variable'
      }

      if (this.fieldSupportsToken(this.activeTemplateFieldKey, token)) {
        return 'Insérer la variable dans le champ actif'
      }

      return 'Variable non utilisable dans ce template'
    },
    rememberFieldState (fieldKey, event) {
      const target = event && event.target
      this.activeTemplateField = {
        key: fieldKey,
        element: target || null,
        start: target && typeof target.selectionStart === 'number' ? target.selectionStart : null,
        end: target && typeof target.selectionEnd === 'number' ? target.selectionEnd : null
      }
    },
    async insertVariable (token) {
      const field = this.activeTemplateField

      if (field && field.key && Object.prototype.hasOwnProperty.call(this.messagingSettings, field.key)) {
        if (!this.fieldSupportsToken(field.key, token)) {
          this.$buefy.notification.open({
            duration: 2500,
            message: `La variable ${token} n'est pas disponible pour ce template`,
            position: 'is-top-right',
            type: 'is-warning',
            hasIcon: true
          })
          return
        }

        const currentValue = this.messagingSettings[field.key] || ''
        const start = typeof field.start === 'number' ? field.start : currentValue.length
        const end = typeof field.end === 'number' ? field.end : start

        this.messagingSettings = {
          ...this.messagingSettings,
          [field.key]: `${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`
        }

        await this.$nextTick()

        if (field.element && typeof field.element.focus === 'function') {
          const nextCursor = start + token.length
          field.element.focus()
          if (typeof field.element.setSelectionRange === 'function') {
            field.element.setSelectionRange(nextCursor, nextCursor)
          }
          this.activeTemplateField = {
            ...field,
            start: nextCursor,
            end: nextCursor
          }
        }

        return
      }

      const copied = await this.copyToClipboard(token)
      this.$buefy.notification.open({
        duration: 2500,
        message: copied
          ? `Variable copiée : ${token}`
          : `Impossible d'insérer automatiquement la variable ${token}`,
        position: 'is-top-right',
        type: copied ? 'is-info' : 'is-warning',
        hasIcon: true
      })
    },
    async copyToClipboard (value) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(value)
          return true
        } catch (error) {
          return false
        }
      }

      return false
    },
    syncMessagingSettings (application) {
      const settings = application?.messaging_settings || {}
      this.messagingSettings = {
        maviepro_response_delay_hours: settings.maviepro_response_delay_hours || 48,
        interlocutor_note_text: settings.interlocutor_note_text || '',
        cse_note_text: settings.cse_note_text || '',
        maviepro_note_text: settings.maviepro_note_text || '',
        cse_auto_reply_text: settings.cse_auto_reply_text || '',
        maviepro_auto_reply_text: settings.maviepro_auto_reply_text || ''
      }
    },
    saveMessagingDomainSettings () {
      if (!this.domain?.uuid || !this.domain?.chat) {
        return
      }

      this.savingDomain = true

      const chatPayload = {
        can_select_respondent: this.domain.chat.can_select_respondent,
        is_activated: this.domain.chat.is_activated,
        is_maviepro_contact_activated: this.mavieproContactActivated,
        max_chat_per_day: this.domain.chat.max_chat_per_day,
        request_start_button_text: this.domain.chat.request_start_button_text,
        interlocutor_response_delay_hours: this.domain.chat.interlocutor_response_delay_hours,
        cse_response_delay_hours: this.domain.chat.cse_response_delay_hours
      }

      Promise.all([
        http.put(`/domain/${this.domain.uuid}/chats`, chatPayload),
        http.put(`/domain/${this.domain.uuid}`, this.buildDomainPayload())
      ]).then(([chatResponse, domainResponse]) => {
        this.$store.commit('SET_DOMAIN', {
          ...domainResponse.data,
          chat: chatResponse.data
        })
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Paramètres messagerie mis à jour avec succès',
          position: 'is-top-right',
          type: 'is-success',
          hasIcon: true
        })
      }).catch((err) => {
        console.log(err)
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour ces paramètres',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      }).finally(() => {
        this.savingDomain = false
      })
    },
    saveMessagingTemplates () {
      const applicationSlug = this.application?.app?.slug || process.env.VUE_APP_APPLICATION
      if (!applicationSlug) {
        return
      }

      this.savingTemplates = true

      const payload = {
        maviepro_response_delay_hours: this.messagingSettings.maviepro_response_delay_hours,
        interlocutor_note_text: this.messagingSettings.interlocutor_note_text,
        cse_note_text: this.messagingSettings.cse_note_text,
        maviepro_note_text: this.messagingSettings.maviepro_note_text,
        cse_auto_reply_text: this.messagingSettings.cse_auto_reply_text,
        maviepro_auto_reply_text: this.messagingSettings.maviepro_auto_reply_text
      }

      http.put(`/application/${applicationSlug}/messaging-settings`, payload).then(({ data }) => {
        this.$store.commit('SET_APPLICATION', data)
        this.syncMessagingSettings(data)
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Templates globaux mis à jour avec succès',
          position: 'is-top-right',
          type: 'is-success',
          hasIcon: true
        })
      }).catch((err) => {
        console.log(err)
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour les templates globaux',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      }).finally(() => {
        this.savingTemplates = false
      })
    },
    buildDomainPayload () {
      const payload = {
        label: this.domain.label,
        color: this.domain.color,
        can_avoid_notifications: this.domain.can_avoid_notifications,
        employees_capacity: this.domain.employees_capacity,
        employees_capacity_is_manual: this.domain.employees_capacity_is_manual
      }

      if (this.domain.contact) {
        payload.contact = {
          is_activated: this.domain.contact.is_activated,
          email: this.domain.contact.email,
          phone: this.domain.contact.phone,
          message: this.domain.contact.message,
          denomination: this.contactDenomination
        }
      }

      return payload
    }
  }
}
</script>
